import React, { useState, useEffect, useCallback, useRef } from 'react';
import socketService from '../services/socketService';
import Layout from '../components/Layout';
import moment from 'moment-timezone';

const SeenSMSupdate = () => {
    // ১. ডাটা এবং লোডিং স্টেট
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ totalPages: 1, currentPage: 1, totalRows: 0 });

    // ২. ইনপুট স্টেট
    const [filterInputs, setFilterInputs] = useState({
        username: '', simno: '', senderID: '', seenBy: '', trxID: '', start_date: '', end_date: '', page: 1
    });

    // ৩. অ্যাক্টিভ ফিল্টার স্টেট (যা বর্তমানে অ্যাপ্লাই করা আছে)
    const [activeFilter, setActiveFilter] = useState({
        username: '', simno: '', senderID: '', seenBy: '', trxID: '', start_date: '', end_date: '', page: 1
    });

    // ৪. অ্যাক্টিভ ফিল্টার রেফারেন্স
    const activeFilterRef = useRef(activeFilter);

    useEffect(() => {
        activeFilterRef.current = activeFilter;
    }, [activeFilter]);

    // Modal States
    const [showModal, setShowModal] = useState(false);
    const [editData, setEditData] = useState({ id: '', value: '' });

    // 🔥 DATA FETCH FUNCTION
    const fetchData = useCallback((filterParams = activeFilterRef.current) => {
        setLoading(true);
        socketService.emit('fetch_alternative_sms_logs', filterParams);
    }, []);

    // 🔥 MAIN SETUP & SOCKET LISTENERS
    useEffect(() => {
        if (!socketService.socket || !socketService.socket.connected) {
            socketService.connect();
        }

        const handleReceiveLogs = (data) => {
            setLogs(data.logs || []);
            setPagination({ 
                totalPages: data.totalPages || 1, 
                currentPage: data.currentPage || 1, 
                totalRows: data.totalRows || 0
            });
            setLoading(false);
        };

        const handleRefresh = (data) => {
            if (data && data.success) {
                setShowModal(false);
            }
            fetchData(activeFilterRef.current); 
        };

        fetchData(activeFilterRef.current);

        socketService.on('receive_alternative_sms_logs', handleReceiveLogs);
        socketService.on('appLogSeenUpdated', handleRefresh);
        socketService.on('new_alternative_log_received', () => fetchData(activeFilterRef.current)); 

        return () => {
            socketService.off('receive_alternative_sms_logs', handleReceiveLogs);
            socketService.off('appLogSeenUpdated', handleRefresh);
            socketService.off('new_alternative_log_received');
        };
    }, [fetchData]); 

    // --- Handlers ---

    // 🔥 ফিক্স: মডাল ওপেন করার লজিক আপডেট
    const openUpdateModal = (id, currentValue) => {
        // যদি unseen বা ফাঁকা থাকে, ইনপুট বক্স খালি থাকবে। অটো time-out বসবে না।
        const val = (currentValue === 'unseen' || !currentValue) ? '' : currentValue;
        setEditData({ id, value: val });
        setShowModal(true);
    };

    const confirmUpdate = () => {
        if (editData.id) {
            // আপনি যা ইনপুট দিবেন (editData.value), ঠিক সেটাই সার্ভারে যাবে
            socketService.emit('updateAppLogSeen', { logId: editData.id, seenBy: editData.value });
        }
    };

    const handleFilterChange = (e) => {
        setFilterInputs({ ...filterInputs, [e.target.name]: e.target.value });
    };

    // APPLY BUTTON
    const handleApply = () => {
        const newFilter = { ...filterInputs, page: 1 };
        setActiveFilter(newFilter);
        activeFilterRef.current = newFilter;
        fetchData(newFilter);
    };

    // RESET BUTTON
    const handleReset = () => {
        const resetData = { username: '', simno: '', senderID: '', seenBy: '', trxID: '', start_date: '', end_date: '', page: 1 };
        setFilterInputs(resetData);
        setActiveFilter(resetData);
        activeFilterRef.current = resetData;
        fetchData(resetData);
    };
    
    // PAGE CHANGE
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            const updatedFilter = { ...activeFilter, page: newPage };
            setActiveFilter(updatedFilter);
            activeFilterRef.current = updatedFilter;
            fetchData(updatedFilter);
        }
    };

    // --- Helpers ---
    const extractAmount = (message) => {
        if (!message) return 0;
        const match = message.match(/(?:Tk|৳)\s?([\d,]+\.?\d*)/i);
        if (match && match[1]) {
            return parseFloat(match[1].replace(/,/g, ''));
        }
        return 0; 
    };

    // 🔥 ফিক্স: কালার লজিক আপডেট
    const getRowClass = (seenBy) => {
        if (!seenBy || seenBy === 'unseen') return ""; // এখন আর অটোমেটিক লাল/time-out কালার হবে না
        const status = seenBy.toLowerCase();
        if (status.includes("fake")) return "row-fake";
        if (status.includes("time-out") || status.includes("timeout")) return "row-timeout"; // শুধু হাতে time-out লিখলে এই কালার পাবে
        if (status.includes("wd") || status.includes("verified")) return "row-verified";
        return "";
    };

    const renderPageNumbers = () => {
        const pages = [];
        const { currentPage, totalPages } = pagination;
        const maxVisibleButtons = 5;
        let startPage = Math.max(1, currentPage - 2);
        let endPage = Math.min(totalPages, startPage + maxVisibleButtons - 1);

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                    <button className="page-link shadow-none" onClick={() => handlePageChange(i)}>{i}</button>
                </li>
            );
        }
        return pages;
    };

    return (
        <Layout>
            <div className="main-content-area py-3 px-10">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="section-title m-0">📲 App Notification update</h5>
                    <div className="count-badge">
                        Total Records: <span>{pagination.totalRows}</span>
                    </div>
                </div>

                {/* Filter Section */}
                <div className="filter-card shadow-sm border-0 mb-4">
                    <div className="row g-2 align-items-end">
                        <div className="col-md-2">
                            <label className="field-label">USERNAME</label>
                            <input type="text" name="username" className="modern-input" placeholder="Search user..." value={filterInputs.username} onChange={handleFilterChange} />
                        </div>
                        <div className="col-md-2">
                            <label className="field-label" style={{color:'#7c3aed'}}>TrxID SEARCH</label>
                            <input type="text" name="trxID" className="modern-input" placeholder="Enter TrxID..." value={filterInputs.trxID} onChange={handleFilterChange} style={{borderColor:'#7c3aed'}} />
                        </div>
                        <div className="col-md-2">
                            <label className="field-label">SEEN STATUS</label>
                            <input type="text" name="seenBy" className="modern-input" placeholder="e.g. time-out, verified" value={filterInputs.seenBy} onChange={handleFilterChange} />
                        </div>
                        <div className="col-md-2">
                            <label className="field-label">START DATE</label>
                            <input type="date" name="start_date" className="modern-input" value={filterInputs.start_date} onChange={handleFilterChange} />
                        </div>
                        <div className="col-md-2">
                            <label className="field-label">END DATE</label>
                            <input type="date" name="end_date" className="modern-input" value={filterInputs.end_date} onChange={handleFilterChange} />
                        </div>
                        <div className="col-md-2 d-flex gap-2">
                            <button className="btn btn-apply fw-bold w-100" onClick={handleApply}>Apply</button>
                            <button className="btn btn-reset fw-bold w-100" onClick={handleReset}>Reset</button>
                        </div>
                    </div>
                </div>

                {/* Table Section */}
                <div className="table-container shadow-sm border-0 mb-3">
                    <div className="table-responsive">
                        <table className="custom-zebra-table">
                            <thead>
                                <tr>
                                    <th className="ps-4">USERNAME</th>
                                    <th>DEVICE & APP</th>
                                    <th>AMOUNT (TK)</th>
                                    <th style={{ minWidth: '400px' }}>MESSAGE CONTENT</th>
                                    <th>DATE & TIME</th>
                                    <th className="text-center">STATUS (EDIT)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="6" className="text-center py-5">Loading...</td></tr>
                                ) : logs.length === 0 ? (
                                    <tr><td colSpan="6" className="text-center py-5 opacity-50">No Data Found</td></tr>
                                ) : logs.map((log) => {
                                    const amount = extractAmount(log.message);
                                    return (
                                        <tr key={log._id} className={getRowClass(log.seenBy)}>
                                            <td className="ps-4">
                                                <div className="user-primary">{log.userId?.username || 'N/A'}</div>
                                            </td>
                                            <td>
                                                <div className="device-secondary text-uppercase">{log.deviceName}</div>
                                                <div className="sender-tag mt-1">{log.senderID}</div>
                                            </td>
                                            <td>
                                                {amount > 0 ? (
                                                    <div className="fw-bold" style={{color: '#0284c7', fontSize: '14px'}}>
                                                        ৳{amount.toLocaleString()}
                                                    </div>
                                                ) : <div className="text-muted opacity-25">--</div>}
                                            </td>
                                            <td className="msg-body py-3">{log.message}</td>
                                            <td>
                                                <div className="date-bold">{moment(log.receivedAt).tz('Asia/Dhaka').format('DD MMM, YYYY')}</div>
                                                <div className="time-muted">{moment(log.receivedAt).tz('Asia/Dhaka').format('hh:mm A')}</div>
                                            </td>
                                            
                                            {/* 🔥 ফিক্স: ডিসপ্লে লজিক আপডেট */}
                                            <td className="text-center">
                                                <div 
                                                    style={{ cursor: 'pointer' }} 
                                                    onClick={() => openUpdateModal(log._id, log.seenBy)}
                                                >
                                                    {log.seenBy && log.seenBy !== 'unseen' ? (
                                                        <span className={`badge-status ${
                                                            log.seenBy.toLowerCase().includes('verified') ? 'bg-seen' : 
                                                            log.seenBy.toLowerCase().includes('fake') ? 'bg-fake' : 'bg-timeout'
                                                        }`}>
                                                            {log.seenBy} 📝
                                                        </span>
                                                    ) : (
                                                        // অটো Time-out এর বদলে এখন 'Unseen' দেখাবে
                                                        <span className="badge-unseen">Unseen 📝</span> 
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pagination */}
                <div className="d-flex justify-content-between align-items-center px-2 py-2">
                    <div className="text-muted small">Showing Page <b>{pagination.currentPage}</b> of <b>{pagination.totalPages}</b></div>
                    <nav>
                        <ul className="pagination pagination-sm m-0 gap-1">
                            <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                                <button className="page-link shadow-none" onClick={() => handlePageChange(pagination.currentPage - 1)}>Prev</button>
                            </li>
                            {renderPageNumbers()}
                            <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                                <button className="page-link shadow-none" onClick={() => handlePageChange(pagination.currentPage + 1)}>Next</button>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="custom-modal-overlay">
                    <div className="custom-modal-dialog">
                        <div className="custom-modal-content">
                            <div className="modal-header bg-light">
                                <h6 className="m-0 fw-bold">Update Log Status</h6>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body p-4">
                                <label className="field-label mb-2">ENTER NEW STATUS</label>
                                <input 
                                    type="text" 
                                    className="modern-input" 
                                    style={{ border: '1px solid #0284c7' }}
                                    value={editData.value} 
                                    onChange={(e) => setEditData({ ...editData, value: e.target.value })}
                                    autoFocus
                                    placeholder="Type Verified, Fake or Anything..."
                                />
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-reset flex-fill py-2" onClick={() => setShowModal(false)}>Cancel</button>
                                <button className="btn btn-apply flex-fill py-2" onClick={confirmUpdate}>Update Now</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                /* Styles borrowed from SeenSMSupdate.js */
                .main-content-area { background-color: #f4f7fa; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .section-title { color: #334155; font-weight: 700; }
                .count-badge { background: #fff; padding: 5px 15px; border-radius: 50px; border: 1px solid #e2e8f0; font-size: 13px; font-weight: 600; }
                .count-badge span { color: #0284c7; font-weight: 800; }
                .filter-card { background: #ffffff; padding: 20px; border-radius: 12px; }
                .field-label { font-size: 11px; font-weight: 700; color: #64748b; margin-bottom: 5px; }
                .modern-input { background: #f8fafc; border: 1px solid #cbd5e1; padding: 8px 12px; border-radius: 8px; font-size: 13px; width: 100%; outline: none; }
                .btn-apply { background: #0284c7; color: white; border-radius: 8px; font-size: 13px; transition: 0.3s; border: none; }
                .btn-apply:hover { background: #0369a1; }
                .btn-reset { background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 13px; }
                .table-container { background: #ffffff; border-radius: 12px; overflow: hidden; }
                .custom-zebra-table { width: 100%; border-collapse: collapse; }
                .custom-zebra-table thead { background: #e2e8f0; color: #475569; }
                .custom-zebra-table th { padding: 14px 15px; font-size: 11px; font-weight: 800; text-transform: uppercase; border-bottom: 2px solid #cbd5e1; text-align: left; }
                .custom-zebra-table tbody tr:nth-child(even) { background-color: #f8fafc; }
                .custom-zebra-table td { padding: 12px 15px; vertical-align: middle; border-bottom: 1px solid #edf2f7; }
                
                .row-fake { background-color: #fff1f2 !important; }
                .row-timeout { background-color: #f5f3ff !important; }
                .row-verified { background-color: #f0fdf4 !important; }

                .user-primary { color: #0369a1; font-weight: 700; font-size: 14px; }
                .device-secondary { font-size: 10px; color: #64748b; font-weight: 800; background: #e2e8f0; display: inline-block; padding: 1px 6px; border-radius: 4px; }
                .sim-num { color: #1e293b; font-weight: 600; font-size: 13px; }
                .sender-tag { color: #6366f1; font-weight: 800; font-size: 11px; }
                .msg-body { color: #334155; font-size: 13px; line-height: 1.5; }
                .date-bold { color: #334155; font-weight: 700; font-size: 13px; }
                .time-muted { font-size: 11px; color: #64748b; }
                .page-link { color: #475569; border: 1px solid #e2e8f0; border-radius: 6px !important; padding: 5px 12px; }
                .page-item.active .page-link { background-color: #0284c7; border-color: #0284c7; color: white; }
                
                .badge-status { padding: 4px 12px; border-radius: 6px; font-size: 11px; font-weight: 700; border: 1px solid transparent; }
                .bg-seen { background: #f0fdf4; color: #16a34a; border-color: #bbf7d0; }
                .bg-fake { background: #fee2e2; color: #dc2626; border-color: #fecaca; }
                .bg-timeout { background: #ede9fe; color: #7c3aed; border-color: #ddd6fe; }
                .badge-unseen { background: #f3f4f6; color: #6b7280; padding: 4px 12px; border-radius: 6px; font-size: 11px; font-weight: 700; border: 1px solid #e5e7eb; }

                /* Modal Overlay */
                .custom-modal-overlay {
                    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                    width: 100%; height: 100%; background: rgba(0, 0, 0, 0.7); 
                    z-index: 99999; display: flex; justify-content: center; align-items: flex-start; 
                    padding-top: 60px; overflow-y: auto; 
                }
                .custom-modal-dialog {
                    background: #fff; width: 90%; max-width: 550px; margin: 0 auto 50px auto; 
                    border-radius: 12px; box-shadow: 0 20px 40px rgba(0,0,0,0.4);
                    position: relative; animation: slideUp 0.3s ease-out; display: flex; flex-direction: column;
                }
                .modal-header { padding: 15px 20px; border-bottom: 1px solid #f1f5f9; display: flex; align-items: center; justify-content: space-between; border-radius: 12px 12px 0 0; }
                .modal-body { padding: 20px; overflow-x: hidden; }
                .modal-footer { padding: 15px 20px; border-top: none; display: flex; gap: 10px; background: #fff; border-radius: 0 0 12px 12px; }
                .btn-close { width: 1em; height: 1em; opacity: 0.5; border: 0; cursor: pointer; background: transparent url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23000'%3e%3cpath d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z'/%3e%3c/svg%3e") center/1em auto no-repeat; }
                @keyframes slideUp { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
            `}</style>
        </Layout>
    );
};

export default SeenSMSupdate;